<template>
    <div class="pp-draw-numbers-rychla-6" data-test="results-regular">
        <div class="pp-draw-numbers-rychla-6__main-game">
            <ul v-for="(group, groupIndex) in groups" :key="group.join('-')" class="pp-draw-numbers-rychla-6__group">
                <h3 v-if="groupIndex < 6" class="pp-draw-numbers-rychla-6__group-title">{{ groupIndex + 1 }}.</h3>
                <h3 v-else class="pp-draw-numbers-rychla-6__group-title pp-draw-numbers-rychla-6__group-title--last">
                    22. číslo
                </h3>
                <ul class="pp-draw-numbers-rychla-6__container">
                    <li v-for="num in group" :key="num" class="pp-draw-numbers-rychla-6__number" data-test="number">
                        {{ num }}
                    </li>
                </ul>
            </ul>
        </div>
        <div class="pp-draw-numbers-rychla-6__sub-game">
            <h3 class="pp-draw-numbers-rychla-6__group-title">Sudá / Lichá</h3>
            <div class="pp-draw-numbers-rychla-6__ratio">
                <div class="pp-draw-numbers-rychla-6__ratio-titles">
                    <h3
                        class="pp-draw-numbers-rychla-6__group-title"
                        :class="`pp-draw-numbers-rychla-6__group-title--${getHeadingColor(
                            evenOddRatio.first,
                            evenOddRatio.second,
                            'blue'
                        )}`"
                    >
                        <span class="pp-draw-numbers-rychla-6__ratio-label">Sudá</span>
                        <span class="pp-draw-numbers-rychla-6__ratio-value">{{ evenOddRatio.first }}</span>
                    </h3>
                    <h3
                        class="pp-draw-numbers-rychla-6__group-title"
                        :class="`pp-draw-numbers-rychla-6__group-title--${getHeadingColor(
                            evenOddRatio.first,
                            evenOddRatio.second,
                            'green'
                        )}`"
                    >
                        <span class="pp-draw-numbers-rychla-6__ratio-label">Lichá</span>
                        <span class="pp-draw-numbers-rychla-6__ratio-value">{{ evenOddRatio.second }}</span>
                    </h3>
                </div>
                <ul class="pp-draw-numbers-rychla-6__ratio-beans">
                    <li
                        v-for="num in DRAWN_NUMBERS"
                        :key="num"
                        :class="`pp-draw-numbers-rychla-6__ratio-bean pp-draw-numbers-rychla-6__ratio-bean--${getBeanColor(
                            num - 1,
                            evenOddRatio
                        )}`"
                    ></li>
                </ul>
            </div>
        </div>
        <div class="pp-draw-numbers-rychla-6__sub-game">
            <h3 class="pp-draw-numbers-rychla-6__group-title">Nízká / Vysoká</h3>
            <div class="pp-draw-numbers-rychla-6__ratio">
                <div class="pp-draw-numbers-rychla-6__ratio-titles">
                    <h3
                        class="pp-draw-numbers-rychla-6__group-title"
                        :class="`pp-draw-numbers-rychla-6__group-title--${getHeadingColor(
                            highLowRatio.first,
                            highLowRatio.second,
                            'blue'
                        )}`"
                    >
                        <span class="pp-draw-numbers-rychla-6__ratio-label">Nízká</span>
                        <span class="pp-draw-numbers-rychla-6__ratio-value">{{ highLowRatio.first }}</span>
                    </h3>
                    <h3
                        class="pp-draw-numbers-rychla-6__group-title"
                        :class="`pp-draw-numbers-rychla-6__group-title--${getHeadingColor(
                            highLowRatio.first,
                            highLowRatio.second,
                            'green'
                        )}`"
                    >
                        <span class="pp-draw-numbers-rychla-6__ratio-label">Vysoká</span>
                        <span class="pp-draw-numbers-rychla-6__ratio-value">{{ highLowRatio.second }}</span>
                    </h3>
                </div>
                <ul class="pp-draw-numbers-rychla-6__ratio-beans">
                    <li
                        v-for="index in DRAWN_NUMBERS"
                        :key="index"
                        :class="`pp-draw-numbers-rychla-6__ratio-bean pp-draw-numbers-rychla-6__ratio-bean--${getBeanColor(
                            index - 1,
                            highLowRatio
                        )}`"
                    ></li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { DrawNumbers } from "@/lotteries/services/model/draw-numbers";
import { computed } from "vue";

const props = defineProps<{
    numbers: DrawNumbers;
}>();

const DRAWN_NUMBERS = 22;
const FIRST_GROUP_SIZE = 6;
const SMALL_GROUP_SIZE = 3;
const LAST_GROUP_INDEX = 21;

const evenOddRatio = computed(() => {
    const evenOdd = props.numbers.mainGameNumbers[0]?.evenOdd;

    return {
        first: evenOdd?.[0] || 0,
        second: evenOdd?.[1] || 0,
    };
});

const highLowRatio = computed(() => {
    const highLow = props.numbers.mainGameNumbers[0]?.lowHigh;

    return {
        first: highLow?.[0] || 0,
        second: highLow?.[1] || 0,
    };
});

const getBeanColor = (index: number, ratio: { first: number; second: number }) => {
    if (ratio.first === ratio.second) {
        return index < ratio.first ? "blue" : "green";
    }

    if (ratio.first > ratio.second) {
        return index < ratio.first ? "blue" : "grey";
    }
    // ratio.first < ratio.second
    return index >= ratio.first ? "green" : "grey";
};

const getHeadingColor = (first: number, second: number, type: "blue" | "green") => {
    if (first === second) {
        return type;
    }

    if (type === "blue") {
        return first > second ? "blue" : "grey";
    }

    // type === "green"
    return first < second ? "green" : "grey";
};

const groups = computed(() => {
    const mainNumbers = props.numbers.mainGameNumbers[0]?.drawingDrums[0];

    if (!mainNumbers) {
        return [];
    }

    return [
        mainNumbers.slice(0, FIRST_GROUP_SIZE), // First 6 numbers
        ...Array.from({ length: 5 }, (_, i) => mainNumbers.slice(6 + i * SMALL_GROUP_SIZE, 9 + i * SMALL_GROUP_SIZE)), // Groups of 3
        mainNumbers.slice(LAST_GROUP_INDEX), // Remaining number
    ];
});
</script>
